import React from 'react';
import { TrashIcon } from '@avila-tek/ui/src';
import { formatCurrency } from '@avila-tek/ui/src/utils/formatCurrency';
import { ShopItem, User } from '../../../../models';

interface EmployeeRowProps {
  user: User;
  type: 'product' | 'plan' | 'benefit';
  price?: number;
  shopList: ShopItem[];
  setShopList: React.Dispatch<React.SetStateAction<ShopItem[]>>;
}

function EmployeeRow({
  user,
  type,
  price,
  shopList,
  setShopList,
}: EmployeeRowProps) {
  const [value, setValue] = React.useState<number>(
    () =>
      shopList?.find(
        (clientProduct) =>
          (clientProduct.client as User)._id === user._id &&
          price === clientProduct.price
      )?.quantity ?? 1
  );

  React.useEffect(() => {
    const refreshBenefitValue = () => {
      if (type === 'benefit') {
        setValue(
          shopList?.find(
            (clientProduct) =>
              (clientProduct.client as User)._id === user._id &&
              price === clientProduct.price
          )?.quantity ?? 0
        );
      }
    };
    refreshBenefitValue();
  }, [shopList]);

  const onChange = (_value: number) => {
    if (type === 'benefit') {
      // take out from shop list if the value is 0
      if (_value <= 0) {
        const newShopList = shopList?.filter(
          (clientProduct) =>
            clientProduct.price !== price ||
            (clientProduct.client as User)._id !== user._id
        );
        return setShopList(newShopList);
      }
      changeBenefitQuantity(_value, price);
    } else {
      // take out from shop list if the value is 0
      if (_value <= 0) {
        const _newShopList = shopList?.filter(
          (clientProduct) => (clientProduct.client as User)._id !== user._id
        );
        return setShopList(_newShopList);
      }
      setValue(_value);
      const newShopList = shopList?.map((clientProduct) => {
        if ((clientProduct.client as User)._id === user._id) {
          return {
            ...clientProduct,
            quantity: _value,
          };
        }
        return clientProduct;
      });
      setShopList(newShopList);
    }
  };

  const changeBenefitQuantity = (_value: number, benefitValue: number) => {
    const benefitPreferencePrice = shopList?.find(
      (clientProduct) =>
        (clientProduct.client as User)._id === user._id &&
        clientProduct.price !== benefitValue
    );
    // validate coupons if the user has coupons with preference price
    if (
      benefitPreferencePrice?.quantity < user?.discountCoupons ||
      (_value <= user?.discountCoupons &&
        benefitPreferencePrice?.quantity <= user?.discountCoupons)
    ) {
      // add benefit with preference price
      const newShopList = shopList?.map((clientProduct) => {
        if (
          (clientProduct.client as User)._id === user._id &&
          clientProduct.price === price
        ) {
          return {
            ...clientProduct,
            quantity: _value,
          };
        }
        return clientProduct;
      });
      setValue(_value);
      return setShopList(newShopList);
    }

    // if user doesnt have coupons with preference price

    // verify if there is a benefit regular price to modify it
    const benefitRegularPrice = shopList?.find(
      (clientProduct) =>
        (clientProduct.client as User)._id === user._id &&
        clientProduct.price === benefitValue
    );

    if (benefitRegularPrice && price === benefitValue) {
      // modify benefit regular price
      const newShopList = shopList?.map((clientProduct) => {
        if (
          (clientProduct.client as User)._id === user._id &&
          clientProduct.price === benefitValue
        ) {
          return {
            ...clientProduct,
            quantity: clientProduct.quantity + _value - clientProduct.quantity,
          };
        }
        return clientProduct;
      });
      return setShopList(newShopList);
    }

    if (price !== benefitValue && !benefitRegularPrice) {
      // else add benefit regular price to shop list
      setShopList([
        ...shopList,
        {
          client: { ...user, _id: user._id },
          quantity: 1,
          price,
        },
      ]);
    }
  };

  const deleteEmployee = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.preventDefault();
    const newShopList = shopList?.filter(
      (clientProduct) => (clientProduct.client as User)._id !== user._id
    );
    setShopList(newShopList);
  };

  const hasDiscountCoupons =
    type === 'benefit' &&
    (user?.discountCoupons ?? 0) - Number(value) > 0 &&
    price !== 5;

  return (
    <div className="w-full border-b border-system-500 flex justify-between mt-3 items-center pl-2">
      <span>
        {user?.firstName} {user?.lastName}
      </span>

      {type === 'product' || type === 'benefit' ? (
        <div className="flex flex-col">
          <div className="flex flex-row h-10  rounded-lg relative bg-transparent mt-1">
            <button
              type="button"
              className="  text-gray-600 h-full w-10 rounded-l cursor-pointer outline-none"
              onClick={(e) => {
                e.preventDefault();
                onChange(value - 1);
              }}
            >
              <span className="m-auto text-2xl text-neutral-cold-400">−</span>
            </button>
            <span className=" flex justify-center  focus:outline-none text-center w-full font-semibold text-md hover:text-black focus:text-black  md:text-base cursor-default items-center text-neutral-cold-400 outline-none">
              <span className="text-purple-300 font-semibold">
                ${formatCurrency(price)}
              </span>{' '}
              <span className="ml-1"> x {value ?? 0}</span>
            </span>

            <button
              type="button"
              className=" text-gray-600  h-full w-10 rounded-r cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                onChange(value + 1);
              }}
            >
              <span className="m-auto text-2xl text-neutral-cold-400 ">+</span>
            </button>
          </div>
          <span className="text-sm font-semibold text-purple-200">
            {hasDiscountCoupons
              ? `Dispone de ${
                  (user?.discountCoupons ?? 0) - value
                } a precio preferencial`
              : null}
          </span>
        </div>
      ) : (
        <TrashIcon
          className="w-6 h-6 text-neutral-cold-400 cursor-pointer hover:text-danger-200"
          onClick={deleteEmployee}
        />
      )}
    </div>
  );
}

export default EmployeeRow;
